import { useState } from "react";
import { Eye, EyeOff, Loader2, UserPlus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { fieldErrorClassName } from "@/components/ui/field";
import { cn } from "@/lib/utils";
import { useAdminTeams, useCreateAdminUser } from "@/hooks/use-admin-users";
import type { AssignableTeamRole, SiteRole } from "@/lib/access";
import { useTranslations } from "@/hooks/use-translations";
import {
	AccessPicker,
	SITE_ROLES,
	TEAM_ROLES,
	useManagerLabels,
	type AccessValue,
} from "./access-picker";

const NO_ACCESS: AccessValue = { fullAccess: false, featureAccess: [] };

export function CreateUserForm() {
	const [name, setName] = useState("");
	const [email, setEmail] = useState("");
	const [password, setPassword] = useState("");
	const [showPassword, setShowPassword] = useState(false);
	const [siteRole, setSiteRole] = useState<SiteRole>("user");
	const [access, setAccess] = useState<AccessValue>(NO_ACCESS);
	const [teamId, setTeamId] = useState("");
	const [teamRole, setTeamRole] = useState<AssignableTeamRole>("member");
	const [teamAccess, setTeamAccess] = useState<AccessValue>(NO_ACCESS);
	const teams = useAdminTeams();
	const create = useCreateAdminUser();
	const t = useTranslations("manager.createUser");
	const labels = useManagerLabels();

	const passwordTooShort = password.length > 0 && password.length < 8;
	const isValid =
		name.trim().length > 0 && email.includes("@") && password.length >= 8 && password.length <= 128;

	const reset = () => {
		setName("");
		setEmail("");
		setPassword("");
		setSiteRole("user");
		setAccess(NO_ACCESS);
		setTeamId("");
		setTeamRole("member");
		setTeamAccess(NO_ACCESS);
	};

	const handleSubmit = (e: React.FormEvent) => {
		e.preventDefault();
		if (!isValid || create.isPending) return;
		create.mutate(
			{
				name: name.trim(),
				email: email.trim(),
				password,
				siteRole,
				...access,
				team: teamId ? { teamId, role: teamRole, ...teamAccess } : undefined,
			},
			{ onSuccess: reset },
		);
	};

	// A section of the page's card (Manager page), styled like the Connectors card.
	return (
		<div>
			<h3 className="text-sm font-medium text-text-primary">{t("title")}</h3>
			<p className="text-sm text-text-tertiary">{t("description")}</p>

			<form onSubmit={handleSubmit} className="mt-4 space-y-5">
				<div className="grid gap-4 md:grid-cols-2">
					<Input
						id="new-user-name"
						label={t("name")}
						value={name}
						onChange={(e) => setName(e.target.value)}
						placeholder={t("namePlaceholder")}
						disabled={create.isPending}
					/>
					<Input
						id="new-user-email"
						label={t("email")}
						type="email"
						value={email}
						onChange={(e) => setEmail(e.target.value)}
						placeholder="user@example.com"
						disabled={create.isPending}
					/>
					<div className="space-y-1.5">
						<label htmlFor="new-user-password" className="block text-sm font-medium text-text-primary">
							{t("password")}
						</label>
						<div className="relative">
							<Input
								id="new-user-password"
								type={showPassword ? "text" : "password"}
								value={password}
								onChange={(e) => setPassword(e.target.value)}
								placeholder={t("passwordPlaceholder")}
								disabled={create.isPending}
								className={cn("pr-10", passwordTooShort && fieldErrorClassName)}
							/>
							<Button
								type="button"
								variant="ghost"
								size="icon-xs"
								onClick={() => setShowPassword((v) => !v)}
								aria-label={t(showPassword ? "hidePassword" : "showPassword")}
								className="absolute right-2 top-1/2 -translate-y-1/2"
							>
								{showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
							</Button>
						</div>
						{passwordTooShort && <p className="text-xs text-danger">{t("passwordTooShort")}</p>}
					</div>
					<div className="space-y-1.5">
						<Select
							id="new-user-role"
							label={t("role")}
							value={siteRole}
							onChange={(e) => setSiteRole(e.target.value as SiteRole)}
							disabled={create.isPending}
						>
							{SITE_ROLES.map((role) => (
								<option key={role} value={role}>
									{labels.siteRole(role)}
								</option>
							))}
						</Select>
						{siteRole === "super_admin" && (
							<p className="text-xs text-text-tertiary">{t("superAdminHint")}</p>
						)}
					</div>
				</div>

				<div className="grid gap-5 md:grid-cols-2">
					<AccessPicker idPrefix="new-user" value={access} onChange={setAccess} disabled={create.isPending} />

					<div className="space-y-4">
						<div className="space-y-1.5">
							<Select
								id="new-user-team"
								label={t("team")}
								value={teamId}
								onChange={(e) => setTeamId(e.target.value)}
								disabled={create.isPending || teams.isLoading}
							>
								<option value="">{t("noTeam")}</option>
								{(teams.data ?? []).map((team) => (
									<option key={team.id} value={team.id}>
										{team.name}
									</option>
								))}
							</Select>
							{teams.isError && (
								<p className="text-xs text-danger">
									{t("teamsError", { error: teams.error instanceof Error ? teams.error.message : "" })}
								</p>
							)}
						</div>
						{teamId && (
							<>
								<Select
									id="new-user-team-role"
									label={t("teamRole")}
									value={teamRole}
									onChange={(e) => setTeamRole(e.target.value as AssignableTeamRole)}
									disabled={create.isPending}
								>
									{TEAM_ROLES.map((role) => (
										<option key={role} value={role}>
											{labels.teamRole(role)}
										</option>
									))}
								</Select>
								<AccessPicker
									idPrefix="new-user-team"
									team
									value={teamAccess}
									onChange={setTeamAccess}
									disabled={create.isPending}
								/>
							</>
						)}
					</div>
				</div>

				<Button type="submit" disabled={!isValid || create.isPending}>
					{create.isPending ? <Loader2 size={16} className="animate-spin" /> : <UserPlus size={16} />}
					{t(create.isPending ? "submitting" : "submit")}
				</Button>
			</form>
		</div>
	);
}
